import React from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export interface PlannedActualInput {
    area?: number | null;
    areaUnit?: string;
    quantity?: number | null;
    quantityUnit?: string;
    rate?: number | null;
    rateUnit?: string;
    durationMinutes?: number | null;
    material?: string | null;
    materialUnit?: string | null;
}

interface PlannedVsActualPanelProps {
    planned: PlannedActualInput;
    actual: PlannedActualInput;
    variancePercent?: number | null;
    varianceExplanation?: string | null;
}

const VARIANCE_THRESHOLD = 30;

const formatValue = (value?: number | null, unit?: string): string => {
    if (value === undefined || value === null || !Number.isFinite(Number(value))) return '—';
    const n = Number(value);
    const rounded = Math.abs(n) >= 100 ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : Number(n.toFixed(2)).toLocaleString();
    return unit ? `${rounded} ${unit}` : rounded;
};

const formatDuration = (minutes?: number | null): string => {
    if (minutes === undefined || minutes === null || !Number.isFinite(Number(minutes))) return '—';
    const total = Number(minutes);
    const h = Math.floor(total / 60);
    const m = Math.round(total % 60);
    if (h === 0) return `${m} min`;
    return `${h}h ${m}m`;
};

/**
 * Planned vs actual comparison for a report, with variance highlighting.
 */
const PlannedVsActualPanel: React.FC<PlannedVsActualPanelProps> = ({
    planned,
    actual,
    variancePercent,
    varianceExplanation,
}) => {
    const variance = Number(variancePercent);
    const hasVariance = Number.isFinite(variance);
    const majorVariance = hasVariance && Math.abs(variance) > VARIANCE_THRESHOLD;

    const rows: Array<{ label: string; planned: string; actual: string }> = [
        { label: 'Area completed', planned: formatValue(planned.area, planned.areaUnit), actual: formatValue(actual.area, actual.areaUnit) },
        { label: 'Material / quantity', planned: formatValue(planned.quantity, planned.quantityUnit), actual: formatValue(actual.quantity, actual.quantityUnit) },
        { label: 'Application rate', planned: formatValue(planned.rate, planned.rateUnit), actual: formatValue(actual.rate, planned.rateUnit) },
        { label: 'Working time', planned: formatDuration(planned.durationMinutes), actual: formatDuration(actual.durationMinutes) },
    ];

    return (
        <div>
            {hasVariance && (
                <div
                    className={`mb-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                        majorVariance
                            ? 'border-orange-200 bg-orange-50 text-orange-800'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    }`}
                >
                    {majorVariance ? (
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                        <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    <div>
                        <p className="font-bold">
                            Quantity variance: {variance > 0 ? '+' : ''}
                            {variance.toFixed(1)}%
                        </p>
                        {varianceExplanation && <p className="mt-0.5 text-xs opacity-90">{varianceExplanation}</p>}
                        {majorVariance && !varianceExplanation && (
                            <p className="mt-0.5 text-xs opacity-90">Exceeds the {VARIANCE_THRESHOLD}% tolerance — explanation not provided.</p>
                        )}
                    </div>
                </div>
            )}

            <div className="overflow-hidden rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-black uppercase tracking-wide text-slate-500">Metric</th>
                            <th className="px-4 py-2 text-right text-xs font-black uppercase tracking-wide text-slate-500">Planned</th>
                            <th className="px-4 py-2 text-right text-xs font-black uppercase tracking-wide text-slate-500">Actual</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                        {rows.map((row) => (
                            <tr key={row.label}>
                                <td className="px-4 py-2 font-semibold text-slate-600">{row.label}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{row.planned}</td>
                                <td className={`px-4 py-2 text-right font-bold ${row.actual === '—' ? 'text-slate-400' : 'text-slate-800'}`}>
                                    {row.actual}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PlannedVsActualPanel;
